import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import {
  GetMachinesDocument,
  GetMachinesQuery,
  GetMachinesQueryVariables,
  MachinesOrderByInput,
} from '../../gql/generated';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { Machine } from '../../types/machines/machine.types';
import { CsvColumn, downloadCsv, toCsv } from '../../utils/csv';
import { formatStatusAge } from '../../utils/formatStatusAge';
import { formatStatus, MACHINE_COLUMN_CONFIGS } from './machineColumnConfig';
import { mapMachineRow } from './mapMachineRow';

/**
 * The backend caps a single page at 1000 rows (applyPagination), so larger
 * result sets are collected page by page.
 */
const PAGE_SIZE = 1000;

/** Safety net so a miscounting backend cannot spin this forever */
const MAX_PAGES = 50;

/**
 * CSV columns are derived from the table column configuration, so any EDMS
 * property added there automatically shows up in the export - including the
 * columns a user has hidden, since an export is meant to be complete.
 */
const CSV_COLUMNS: Array<CsvColumn<Machine>> = MACHINE_COLUMN_CONFIGS.map(
  (config) => ({
    header: config.header,
    value: (row: Machine) => {
      // Machine state is EDMS's `state` verbatim, as in the table. An exported
      // file is read far from the tooltip that would explain a synthetic
      // offline, so the qualifier travels with the value.
      if (config.id === 'status') {
        if (!row.statusIsSynthetic) return formatStatus(row.status);
        const last = row.lastRecordedStatus
          ? formatStatus(row.lastRecordedStatus)
          : 'none recorded';
        const age = formatStatusAge(row.lastRecordedAt);
        return `${formatStatus(row.status)} (no heartbeat; last reported ${last}${age ? `, ${age}` : ''})`;
      }

      const key = (config.accessorKey ?? config.id) as keyof Machine;
      const value = row[key];

      // Mirror the table: an empty cell that means something says so
      if (value === null || value === undefined || value === '') {
        return config.emptyText ?? '';
      }

      return typeof value === 'string' || typeof value === 'number'
        ? value
        : String(value);
    },
  })
);

export interface ExportMachinesOptions {
  /** Restrict the export to these machine ids (used for selection exports) */
  machineIds?: string[];
  /** Sort the exported rows the same way the table is sorted */
  orderBy?: MachinesOrderByInput;
}

function buildFileName(machineCount: number): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `pulse-machines-${stamp}-${machineCount}.csv`;
}

/**
 * Exports machines to CSV.
 *
 * The export always covers every row matching the active filters - not just
 * the page currently on screen - so the file can be handed over as a complete
 * machine list (for example to target an EDMS layer deployment).
 */
export function useMachinesExport() {
  const client = useApolloClient();
  const { graphQLFilter } = useMachineFilters();
  const [exporting, setExporting] = useState(false);

  const fetchAllMachines = useCallback(
    async (options?: ExportMachinesOptions): Promise<Machine[]> => {
      const filter = {
        ...(graphQLFilter ?? {}),
        ...(options?.machineIds?.length
          ? { machineIds: options.machineIds }
          : {}),
      };

      const rows: Machine[] = [];
      let offset = 0;

      for (let page = 0; page < MAX_PAGES; page++) {
        const { data } = await client.query<
          GetMachinesQuery,
          GetMachinesQueryVariables
        >({
          query: GetMachinesDocument,
          variables: {
            filter: Object.keys(filter).length > 0 ? filter : undefined,
            orderBy: options?.orderBy,
            pagination: { offset, limit: PAGE_SIZE },
          },
          fetchPolicy: 'network-only',
        });

        const pageRows = data?.machines?.rows ?? [];
        rows.push(...pageRows.map(mapMachineRow));

        const total = data?.machines?.count ?? rows.length;
        if (pageRows.length === 0 || rows.length >= total) break;

        offset += PAGE_SIZE;
      }

      return rows;
    },
    [client, graphQLFilter]
  );

  const exportMachines = useCallback(
    async (options?: ExportMachinesOptions): Promise<number> => {
      setExporting(true);
      try {
        const machines = await fetchAllMachines(options);
        downloadCsv(
          buildFileName(machines.length),
          toCsv(machines, CSV_COLUMNS)
        );
        return machines.length;
      } finally {
        setExporting(false);
      }
    },
    [fetchAllMachines]
  );

  return { exportMachines, exporting };
}
